import { useCallback, useEffect, useState } from 'react'

import Logo from './img/logo.svg'
import { useStore } from 'store'
import { observer } from 'mobx-react-lite'
import axios from 'axios'
import { countdown, parseError } from 'utils/helpers'
import Loader from 'components/Base/Loader'
import { useNavigate } from 'react-router-dom'
import ConfirmationCode from 'components/ConfirmationCode/ConfirmationCode'
import PhoneInput from 'react-phone-input-2'
import GoogleSign from 'components/GoogleSign/GoogleSign'
import BaseModal from 'components/Base/BaseModal'

const SignUp = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneCode, setPhoneCode] = useState('');
    const [userType, setUserType] = useState('provider');
    const [modalState, setModalState] = useState("login");
    const [resetStep, setResetStep] = useState(0);
    const [resetCode, setResetCode] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);
    const [isOTPSend, setIsOTPSend] = useState(false);
    const [error, setError] = useState({});

    const store = useStore();

    const userTypeMapping = {
        Customer: 'customer',
        Caregiver: 'provider',
        Partner: 'partner',
    };
    const navigate = useNavigate();
    useEffect(() => {
        document.body.classList.add("tailwind-body");

        return () => {
            document.body.classList.remove("tailwind-body");
        };
    }, []);


    const hideModal = useCallback(() => {
        store.auth.toggleModal(false);
    }, [store.auth]);

    const signIn = useCallback(() => {

        axios
            .post(`/auth/signin/${userType}`, { email, password })
            .then(({ data }) => {
                store.auth.authorize(data.token, userType, data.backgroundCheckResult);
            })
            .catch((e) => {
                setError(parseError(e));
            });
    }, [email, password, userType, store.auth]);

    const startReset = useCallback(() => {
        setModalState("reset");
        setResetStep(0);
        setResetCode('');
    }, []);

    const resetPassword = useCallback(() => {
        setIsLoading(true);
        setError({});

        if (resetStep === 0) {
            axios
                .post(`/auth/reset/${userType}`, { email })
                .then(({ data }) => {
                    store.notif.create({
                        title: 'Reset started',
                        content:
                            'An email has been sent to you to confirm your password reset. Check your email and enter the code from the letter in the code below',
                        type: 'info',
                        autoClose: 15000,
                    });
                    setResetStep(1);
                    // setIsReset(false);
                })
                .catch((e) => {
                    setError(parseError(e));
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }

        if (resetStep === 1) {
            setResetStep(2);
            setIsLoading(false);
        }

        if (resetStep === 2) {
            axios
                .post(`/auth/reset/confirm`, {
                    email,
                    password,
                    code: resetCode.join(''),
                })
                .then(({ data }) => {
                    setResetStep(2);
                    setModalState("login");

                    store.notif.create({
                        title: 'Success',
                        content: 'Password reset successfully!',
                        type: 'success',
                        autoClose: 10000,
                    });
                })
                .catch((e) => {
                    const status = e.response?.status;
                    if (status === 422) {
                        setResetStep(1);
                        return store.notif.create({
                            title: 'Reset error',
                            content: e.response.data.error,
                            type: 'error',
                            autoClose: 10000,
                        });
                    } else if (status === 426) {
                        setResetStep(0);
                        return store.notif.create({
                            title: 'Reset error',
                            content: e.response.data.error,
                            type: 'error',
                            autoClose: 10000,
                        });
                    } else {
                        setError(parseError(e));
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [store.notif, email, password, userType, resetCode, resetStep]);

    const numberSignIn = async () => {
        if (!phoneNumber) {
            setError({ message: "Phone number is required." });
            return;
        }
        try {
            setError({})
            await axios.post(`/auth/signin/number/${userType}`, {
                resend: isOTPSend, number: phoneNumber
            });

            setTimeLeft(60)
            countdown(setTimeLeft)
            setIsOTPSend(true)
        } catch (error) {
            setError(parseError(error));
            console.error("Failed to send OTP:", error.message);
        }
    };

    const confirmNumberSignIn = async () => {
        if (!phoneCode) {
            setError({ message: "Verification code is required." });
            return;
        }
        axios.post(`/auth/signin/number/confirm/${userType}`, { code: phoneCode, number: phoneNumber })
            .then(({ data }) => {
                store.auth.authorize(data.token, userType, data.backgroundCheckResult);
            })
            .catch((e) => {
                setError(parseError(e));
                console.error("Validation failed", e);
            });
    };

    const googleSignIn = useCallback(
        ({ access_token }) => {
            axios
                .post(`/auth/signin/${userType}/google`, { access_token })
                .then(({ data }) => {
                    store.auth.authorize(data.token, userType, data.backgroundCheckResult);
                    navigate('/');
                })
                .catch((e) => {
                    const message = e.response?.data?.error || 'is invalid';
                    setError({ type: 'Google Auth', message });
                });
        },
        [userType, store.auth]
    );

    const closeModal = useCallback(() => {
        setModalState("login");
        setPhoneNumber('');
        setPhoneCode('');
        setIsOTPSend(false);
        setTimeLeft(0);
    }
        , []);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12" style={{ width: '512px', padding: '0 32px' }}>
            <div className="sm:mx-auto sm:w-full">
                <a href="/" className="flex justify-center items-center space-x-2 mb-8">
                    <img
                        src={Logo}
                        alt="Emissary Care"
                        className="h-12 w-auto"
                    />
                    <span className="text-xl font-semibold bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                        Emissary Care
                    </span>
                </a>
                {/* <h2 className="mt-6 text-center text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                    Create your account
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                    Start your journey to better care today
                </p> */}
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <form className="space-y-6" onSubmit={signIn}>
                        {/* User type selection */}
                        <div className="mt-4">
                            <label htmlFor="userType" className="block text-sm font-medium text-gray-700">
                                Select user type
                            </label>
                            <select
                                id="userType"
                                name="userType"
                                value={userType}
                                onChange={(e) => setUserType(e.target.value)}
                                className="input-primary w-full p-1 mt-2"
                            >
                                <option value="customer">Customer</option>
                                <option value="provider">Caregiver</option>
                                <option value="partner">Partner</option>
                            </select>
                        </div>
                        {/* <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">

                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                    First name
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="firstName"
                                        name="firstName"
                                        type="text"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="input-primary w-full"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                    Last name
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="lastName"
                                        name="lastName"
                                        type="text"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="input-primary w-full"
                                    />
                                </div>
                            </div>
                        </div> */}

                        <div className="mt-6 flex justify-center">
                            <GoogleSign onSign={googleSignIn} type="in" />
                        </div>

                        <div className="mt-6 flex items-center justify-center">
                            <div className="w-1/3 border-t border-gray-300"></div>
                            <span className="mx-4 text-sm text-gray-600">or</span>
                            <div className="w-1/3 border-t border-gray-300"></div>
                        </div>

                        <div className="mt-6 flex justify-center">
                            <button
                                type="button"
                                className="btn-primary w-full bg-gray-500 text-white py-2 px-4 rounded-md shadow-md flex justify-center items-center space-x-2"
                                onClick={() => setModalState("phone")}
                            >
                                <span>Login with Phone Number</span>
                            </button>
                        </div>

                        <div className="mt-6 flex items-center justify-center">
                            <div className="w-1/3 border-t border-gray-300"></div>
                            <span className="mx-4 text-sm text-gray-600">or</span>
                            <div className="w-1/3 border-t border-gray-300"></div>
                        </div>

                        <div className='!mt-10'>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email address
                            </label>
                            <div className="mt-1">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-primary w-full"
                                />
                            </div>
                        </div>


                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Password
                            </label>
                            <div className="mt-1">
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input-primary w-full"
                                />
                            </div>
                        </div>

                        {error.message && (
                            <div>
                                <b>{error.type}</b> {error.message}
                            </div>
                        )}

                        <div className="mt-6 flex justify-center">
                            <button
                                type="button"
                                className="btn-primary w-full bg-gray-500 text-white py-2 px-4 rounded-md shadow-md"
                                onClick={signIn}
                            >
                                <span>Sign in</span>

                            </button>
                        </div>
                        <div className="reset cursor-pointer" onClick={startReset}>
                            Forgot password?
                        </div>

                    </form>
                </div>


                {
                    modalState === "reset" && (
                        <BaseModal show={true} onClose={closeModal}>
                            <div className="w-full max-w-md mx-auto bg-white p-6 rounded-lg  space-y-4">
                                <h4 className="text-xl font-semibold text-gray-800">Reset password</h4>


                                {resetStep === 0 && (
                                    <div>
                                        <div className="mt-4">
                                            <label htmlFor="userType" className="block text-sm font-medium text-gray-700">
                                                Select user type
                                            </label>
                                            <select
                                                id="userType"
                                                name="userType"
                                                value={userType}
                                                onChange={(e) => setUserType(e.target.value)}
                                                className="input-primary w-full p-1 mt-2"
                                            >
                                                <option value="provider">Caregiver</option>
                                                <option value="partner">Partner</option>
                                                <option value="customer">Customer</option>
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {resetStep === 0 && (
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onChange={(e) => setEmail(e.target.value)}
                                        value={email}
                                    />
                                )}

                                {resetStep === 1 && <ConfirmationCode setConfirm={setResetCode} />}

                                {resetStep === 2 && (
                                    <input
                                        type="password"
                                        placeholder="New password"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onChange={(e) => setPassword(e.target.value)}
                                        value={password}
                                    />
                                )}

                                {error.message && (
                                    <div className="text-red-500 text-sm">
                                        <b>{error.type}</b> {error.message}
                                    </div>
                                )}

                                <button
                                    onClick={resetPassword}
                                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition duration-200"
                                >
                                    {['Reset', 'Enter', 'Save'][resetStep]}
                                </button>

                                <div
                                    onClick={() => setModalState("login")}
                                    className="text-center text-sm text-blue-600 cursor-pointer hover:underline"
                                >
                                    Back to login
                                </div>

                                <Loader show={isLoading} />
                            </div>
                        </BaseModal>
                    )
                }





                {
                    modalState === "phone" && (
                        <BaseModal show={true} onClose={closeModal} width={400} className="phone-modal">
                            <div className="form">
                                <p className="phone-title">Please enter your phone number</p>

                                {error.message && (
                                    <div className="error-message">
                                        <b>{error.type}</b> {error.message}
                                    </div>
                                )}

                                <PhoneInput
                                    className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 phone-input"
                                    country={'us'}
                                    specialLabel=""
                                    value={phoneNumber.toString()}
                                    onChange={(phone) => setPhoneNumber(phone)}
                                />

                                {!isOTPSend && (
                                    <button
                                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 w-full mt-4"
                                        onClick={numberSignIn}
                                    >
                                        Receive code
                                    </button>
                                )}

                                {isOTPSend && (
                                    <>
                                        <ConfirmationCode setConfirm={setPhoneCode} />

                                        {timeLeft > 0 ? (
                                            <div className="flex items-center justify-center gap-2 text-gray-600 mt-4">
                                                <p className="timer-text">Resend available in:</p>
                                                <span className="font-medium">{timeLeft}s</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center mt-4">
                                                <button
                                                    className="text-blue-600 hover:text-blue-800"
                                                    onClick={numberSignIn}
                                                >
                                                    Resend confirmation code
                                                </button>
                                            </div>
                                        )}

                                        <button
                                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 w-full mt-4"
                                            onClick={confirmNumberSignIn}
                                            disabled={!phoneCode}
                                        >
                                            Confirm code
                                        </button>
                                    </>
                                )}
                            </div>
                        </BaseModal>
                    )
                }
            </div>
            <Loader show={isLoading} />
        </div>
    )
}

export default observer(SignUp)